import React, { useEffect, useState } from "react";
import api from "../api/client";
import { backdropClose } from "./ModalBackdrop";

/* Shared combined offboard: lock a departing user out of BOTH on-prem AD and
 * Microsoft 365 in one action. Start from whichever side you know (pass `ad`
 * or `m365`); the other is auto-matched by UPN. Used from the AD page and the
 * Microsoft 365 dashboard. */

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" {...backdropClose(onClose)}>
      <div
        className="bg-white dark:bg-gray-800 dark:text-gray-100 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}

export default function OffboardUserModal({
  customerId,
  upn,
  displayName,
  ad,
  m365,
  onClose,
  onDone,
}: {
  customerId: number;
  upn: string;
  displayName?: string;
  ad?: { id: number; sam: string } | null;
  m365?: { id: number; on_prem: boolean } | null;
  onClose: () => void;
  onDone: (summary: string) => void;
}) {
  const [adMatch, setAdMatch] = useState<{ id: number; sam: string } | null>(ad || null);
  const [m365Match, setM365Match] = useState<{ id: number; on_prem: boolean } | null>(m365 || null);
  const [resolving, setResolving] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pw, setPw] = useState<string | null>(null);
  const [note, setNote] = useState("");

  const [adOff, setAdOff] = useState({
    disable_account: true,
    reset_password: true,
    revoke_sessions: true,
    strip_group_memberships: true,
    move_to_disabled_ou: false,
  });
  const [m365Off, setM365Off] = useState({
    disable: true,
    revoke_sessions: true,
    reset_mfa: true,
    remove_licenses: true,
  });

  // Auto-match the counterpart by UPN.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setResolving(true);
      const tasks: Promise<void>[] = [];
      if (!ad && upn) {
        tasks.push(
          api
            .get("/api/v1/ad/users", { params: { customer_id: customerId, search: upn } })
            .then((r) => {
              const rows: any[] = Array.isArray(r.data) ? r.data : r.data?.items || [];
              const hit = rows.find(
                (u) =>
                  (u.user_principal_name || "").toLowerCase() === upn.toLowerCase() ||
                  (u.email || "").toLowerCase() === upn.toLowerCase(),
              );
              if (hit && !cancelled) setAdMatch({ id: hit.id, sam: hit.sam_account_name });
            })
            .catch(() => {}),
        );
      }
      if (!m365 && upn) {
        tasks.push(
          api
            .get("/api/v1/m365/user-lookup", { params: { customer_id: customerId, upn } })
            .then((r) => {
              if (r.data?.found && !cancelled)
                setM365Match({ id: r.data.id, on_prem: !!r.data.on_premises_sync_enabled });
            })
            .catch(() => {}),
        );
      }
      await Promise.all(tasks);
      if (!cancelled) setResolving(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = async () => {
    setBusy(true);
    setError("");
    const summary: string[] = [];
    // AD half (queued on the runner)
    if (adMatch) {
      const anyAd = Object.values(adOff).some(Boolean);
      if (anyAd) {
        try {
          const r = await api.post(`/api/v1/ad/users/${adMatch.id}/offboard`, { ...adOff, note });
          if (r.data?.generated_password) setPw(r.data.generated_password);
          summary.push("AD offboard queued");
        } catch (e: any) {
          summary.push(`AD offboard failed: ${e.response?.data?.detail || "error"}`);
        }
      }
    }
    // M365 half (synchronous Graph)
    if (m365Match) {
      const anyM365 =
        m365Off.disable || m365Off.revoke_sessions || m365Off.reset_mfa || m365Off.remove_licenses;
      if (anyM365) {
        try {
          await api.post(`/api/v1/m365/users/${m365Match.id}/offboard`, m365Off);
          summary.push("Microsoft 365 offboarded");
        } catch (e: any) {
          summary.push(`M365 offboard failed: ${e.response?.data?.detail || "error"}`);
        }
      }
    }
    setBusy(false);
    onDone(summary.join("; ") || "No steps selected");
    if (!pw) onClose(); // keep modal open only to reveal a one-time AD password
  };

  const checkRow = (checked: boolean, set: () => void, label: string) => (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={checked} onChange={set} />
      {label}
    </label>
  );

  if (pw) {
    return (
      <Modal title="Offboard — AD password reset" onClose={onClose}>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
          New AD password for <strong>{adMatch?.sam}</strong> — shown once.
        </p>
        <div className="bg-gray-900 text-green-300 font-mono text-sm rounded px-3 py-2 select-all break-all mb-3">
          {pw}
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => navigator.clipboard?.writeText(pw)}
            className="border dark:border-gray-600 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            Copy
          </button>
          <button onClick={onClose} className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg">
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Offboard ${displayName || upn}`} onClose={onClose}>
      {resolving ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-4">Finding accounts…</p>
      ) : (
        <>
          {!adMatch && !m365Match && (
            <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
              No AD or Microsoft 365 account resolved for {upn}.
            </p>
          )}

          {adMatch && (
            <div className="mb-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                🖥 On-prem Active Directory — {adMatch.sam}
              </div>
              <div className="space-y-1.5">
                {checkRow(adOff.disable_account, () => setAdOff({ ...adOff, disable_account: !adOff.disable_account }), "Disable AD account")}
                {checkRow(adOff.reset_password, () => setAdOff({ ...adOff, reset_password: !adOff.reset_password }), "Reset password (random)")}
                {checkRow(adOff.revoke_sessions, () => setAdOff({ ...adOff, revoke_sessions: !adOff.revoke_sessions }), "Revoke sessions / Kerberos tickets")}
                {checkRow(adOff.strip_group_memberships, () => setAdOff({ ...adOff, strip_group_memberships: !adOff.strip_group_memberships }), "Remove from all non-primary groups")}
                {checkRow(adOff.move_to_disabled_ou, () => setAdOff({ ...adOff, move_to_disabled_ou: !adOff.move_to_disabled_ou }), 'Move to "Disabled Users" OU')}
              </div>
            </div>
          )}

          {m365Match && (
            <div className="mb-3 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20 p-3">
              <div className="text-xs font-semibold text-indigo-800 dark:text-indigo-200 mb-1.5">
                ☁ Microsoft 365 — {upn}
              </div>
              <div className="space-y-1.5">
                {checkRow(m365Off.disable, () => setM365Off({ ...m365Off, disable: !m365Off.disable }), "Block sign-in")}
                {checkRow(m365Off.revoke_sessions, () => setM365Off({ ...m365Off, revoke_sessions: !m365Off.revoke_sessions }), "Revoke active sessions")}
                {checkRow(m365Off.reset_mfa, () => setM365Off({ ...m365Off, reset_mfa: !m365Off.reset_mfa }), "Reset (clear) MFA methods")}
                {checkRow(m365Off.remove_licenses, () => setM365Off({ ...m365Off, remove_licenses: !m365Off.remove_licenses }), "Remove all licenses")}
              </div>
              {m365Match.on_prem && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                  Hybrid user — sign-in is blocked in AD (authoritative); cloud steps still revoke
                  sessions, clear MFA, and free licenses.
                </p>
              )}
            </div>
          )}

          <textarea
            placeholder="Optional note for the audit log…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm mb-3 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            rows={2}
          />
          {error && <p className="text-sm text-red-600 dark:text-red-300 mb-2">{error}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="border dark:border-gray-600 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40">
              Cancel
            </button>
            <button
              onClick={confirm}
              disabled={busy || (!adMatch && !m365Match)}
              className="bg-red-600 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {busy ? "Offboarding…" : "Offboard user"}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
